import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import { Box, Flex, Text } from '@cad-arc';
import { formatSensorAlertClockTime, getSensorEventTitle } from './helpers';
import { SensorSeverityDot } from './sensor-severity-dot';
import { useSensorPriority } from './use-sensor-priority';
import { useOpenSensorEventPanel } from './use-open-sensor-event-panel';

/**
 * One row in the sensor panel's recent-events list: time, severity and what happened. Clicking it
 * opens the sensor event's panel, which carries the event's detail and actions.
 */
export const SensorAlertRow = observer(function SensorAlertRow({
    alert,
}: {
    alert: SensorAlertBundle;
}): React.ReactElement {
    const openSensorEventPanel = useOpenSensorEventPanel();
    const { isPriority1 } = useSensorPriority(alert);
    // The design appends a zone ("— loading bay"). Alerts carry no zone, so the title stands alone.
    const title = getSensorEventTitle(alert);

    return (
        /* Fixed height, so a short last page does not change the section's size under the pager. */
        <Flex
            as="button"
            type="button"
            alignItems="center"
            gap={2}
            width="100%"
            height="40px"
            paddingX={3}
            cursor="pointer"
            textAlign="left"
            backgroundColor="surface.foreground"
            _hover={{ backgroundColor: 'interactive.hover' }}
            onClick={() => openSensorEventPanel({ alertKey: alert.alertKey })}
            data-testid="SENSOR_ALERT_ROW"
        >
            <Box width="40px" flexShrink={0}>
                <Text variant="caption" color="text.secondary" fontFamily="mono">
                    {formatSensorAlertClockTime(alert.createdDateUtc)}
                </Text>
            </Box>
            <SensorSeverityDot size={6} isPriority1={isPriority1} />
            <Text variant="bodyMd" fontWeight="bold" noOfLines={1}>
                {title}
            </Text>
        </Flex>
    );
});
